import type { CoverageView } from './coverage.types';

/**
 * The outstanding premium, when the insured is the one who owes it.
 *
 * Three conditions, all necessary. `isPaid === false` is the debt; `agencyBilled`
 * false is who owes it -- an agency-billed policy must never prompt the insured;
 * and `isInvoiceAvailable` is whether there is anything to pay against yet.
 *
 * No due date: the policy read carries none. Rather than invent one, this reports
 * only what the payload supports -- how much, and on which policy.
 */
export interface PaymentDue {
  readonly policyId: string;
  readonly reference: string;
  readonly product: string;
  readonly amount: number;
}

export function selectPaymentDue(view: CoverageView): PaymentDue | undefined {
  const owing = view.groups
    .map((group) => group.current)
    .find(
      (line) =>
        line !== undefined &&
        line.kind === 'policy' &&
        line.isPaid === false &&
        line.agencyBilled !== true &&
        line.isInvoiceAvailable !== false &&
        // Greater than zero, not merely present. A fully credited or zero-premium
        // policy is unpaid in the data but has nothing to pay, and would otherwise
        // get a "Payment due" tile offering to collect $0 -- the existing rail gate
        // has always required totalPremium > 0.
        (line.premium ?? 0) > 0
    );

  if (!owing) {
    return undefined;
  }

  return {
    policyId: owing.id,
    reference: owing.reference,
    product: owing.product,
    amount: owing.premium as number,
  };
}
